import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  useGetSchoolPocsQuery,
  useCreateSchoolPocMutation,
  useUpdateSchoolPocMutation,
} from "../../../../../../../services/school";
import { useUpdateSchoolLabPocMutation } from "../../../../../../../services/school/lab/useUpdateLabPocDetails";
import { useGetSchoolLabPocBySchoolIdQuery } from "../../../../../../../services/school/lab/useGetSchoolLabPocBySchoolIdQuery";
import { useCreateLabPocDetailsMutation } from "../../../../../../../services/school/lab/useAddLabPocDetails";
import { schema, FormValues } from "./validations";
import {
  SchoolPoc,
  POC_NAME,
  POC_DESIGNATION,
  POC_DEPARTMENT,
  POC_PHONE,
  POC_EMAIL,
} from "./consts";
import { toast } from "sonner";

export const useAddEditSchoolPoc = () => {
  const { id: schoolId } = useParams<{ id: string }>();
  const [isAddPocDialogOpen, setIsAddPocDialogOpen] = useState(false);
  const [localPocs, setLocalPocs] = useState<SchoolPoc[]>([]);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});

  // Fetch existing school POCs
  const { data: schoolPocsData, isLoading: isLoadingPocs } =
    useGetSchoolPocsQuery(schoolId || "", { enabled: !!schoolId });

  const schoolPocs = schoolPocsData?.data || [];

  // Fetch lab-specific POCs by schoolId (separate API)
  const { data: schoolLabPocsData, isLoading: isLoadingLabPocs } =
    useGetSchoolLabPocBySchoolIdQuery(schoolId || "", { enabled: !!schoolId });

  const schoolLabPocs = schoolLabPocsData?.data || [];

  // Initialize local POCs when schoolPocs data loads
  useEffect(() => {
    // Prefer lab POC API data if present; otherwise fallback to generic schoolPocs
    if (schoolLabPocs.length > 0) {
      // Map API shape to our SchoolPoc interface. API items may not include ids.
      const mapped = schoolLabPocs.map((poc: any, idx: number) => ({
        id: poc._id || `lab-${idx}`,
        pocName: poc.pocName || "",
        pocDesignation: poc.pocDesignation || "",
        pocDepartment: poc.pocDepartment || "",
        pocPhone: poc.pocPhone || "",
        pocEmail: poc.pocEmail || "",
        schoolId: schoolId || "",
      }));
      setLocalPocs(mapped);
      return;
    }

    if (schoolPocs.length > 0) {
      setLocalPocs(schoolPocs);
    }
  }, [schoolPocs, schoolLabPocs, schoolId]);


  // Update POCs mutation
  const { mutate: updatePocs, isPending: isUpdatingPocs } =
    useUpdateSchoolPocMutation({
      onSuccess: () => {
        toast.success("POCs updated successfully");
      },
      onError: (error) => {
        toast.error(
          error.response.data?.issues?.message ||
            error.response.data.message ||
            "Failed to update POCs"
        );
      },
    });

  // Lab-specific update mutation (updates lab POC entries using their pocId)
  const { mutate: updateLabPocs, isPending: isUpdatingLabPocs } =
    useUpdateSchoolLabPocMutation({
      onSuccess: () => {
        toast.success("Lab POCs updated successfully");
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.message || "Failed to update lab POCs"
        );
      },
    });

  // Form for adding new POC
  const addPocForm = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      pocName: "",
      pocDesignation: "",
      pocPhone: "",
      pocEmail: "",
    },
  });

  const handleAddPoc = (values: FormValues) => {
    if (!schoolId) return;

    // Use the existing createPoc mutation to create a school POC list entry.
    createLabPocDetails({
      schoolId,
      pocList: [
        {
          pocName: values.pocName,
          pocDesignation: values.pocDesignation,
          pocPhone: values.pocPhone,
          pocEmail: values.pocEmail,
        },
      ],
    });
  };

  // Lab POC details mutation (different API shape). We instantiate it here and
  // expose the mutate function for callers who want to create lab-specific POC
  // configuration (sections/curriculum/year). We don't call it automatically
  // because the UI currently collects different fields; the component can call
  // `createLabPocDetails` with the proper payload when needed.
  const { mutate: createLabPocDetails, isPending: isCreatingLabPocDetails } =
    useCreateLabPocDetailsMutation({
      onSuccess: () => {
        toast.success("Lab POC details created successfully");
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.message || "Failed to create lab POC details"
        );
      },
    });

  const handleUpdatePocs = (updatedPocs: SchoolPoc[]) => {
    if (!schoolId) return;

    const pocData = updatedPocs
      .filter((poc) => poc.id) // Ensure poc.id exists
      .map((poc) => ({
        schoolId: schoolId,
        pocId: poc.id!, // All POCs here are existing ones with valid IDs
        pocName: poc.pocName,
        pocDesignation: poc.pocDesignation,
        pocPhone: poc.pocPhone,
        pocEmail: poc.pocEmail,
        isDeleted: false,
      }));

    // Call lab-specific update endpoint with { pocList: [...] }
    updateLabPocs({ pocList: pocData });
  };

  // Validation using Zod
  const validatePocData = (poc: SchoolPoc): boolean => {
    try {
      const pocData = {
        [POC_NAME]: poc.pocName,
        [POC_DESIGNATION]: poc.pocDesignation,
        [POC_PHONE]: poc.pocPhone,
        [POC_EMAIL]: poc.pocEmail,
      };

      schema.parse(pocData);
      return true;
    } catch (error: any) {
      const errors: { [key: string]: string } = {};

      if (error.errors) {
        error.errors.forEach((err: any) => {
          const field = err.path[0];
          errors[`${poc.id}-${field}`] = err.message;
        });
      }

      setValidationErrors((prev) => ({ ...prev, ...errors }));
      return false;
    }
  };

  const validateAllPocs = (): boolean => {
    setValidationErrors({}); // Clear previous errors
    const allValid = localPocs.every((poc) => validatePocData(poc));
    return allValid;
  };

  const handleSaveChanges = () => {

    // Validate all POCs before saving
    if (!validateAllPocs()) {
      return; // Don't save if validation fails
    }


    // Separate new and existing POCs
    const newPocs = localPocs.filter((poc) => poc.id?.startsWith("temp-"));
    const existingPocs = localPocs.filter(
      (poc) => poc.id && !poc.id.startsWith("temp-")
    );


    // Create new POCs
    if (newPocs.length > 0) {
      const pocData = newPocs.map((poc) => ({
        pocName: poc.pocName,
        pocDesignation: poc.pocDesignation,
        pocPhone: poc.pocPhone,
        pocEmail: poc.pocEmail,
      }));

      createLabPocDetails({
        schoolId: schoolId || "",
        pocList: pocData,
      });
    }

    // Update existing POCs
    if (existingPocs.length > 0) {
      handleUpdatePocs(existingPocs);
    }
  };

  const handleAddNewRow = () => {
    setIsAddingNew(true);
    const newPoc: SchoolPoc = {
      id: `temp-${Date.now()}`, // Temporary ID for new entries
      pocName: "",
      pocDesignation: "",
      pocPhone: "",
      pocEmail: "",
      schoolId: "", // Will be set when saving
    };
    setLocalPocs([...localPocs, newPoc]);
  };

  const handleDeletePocLocal = (pocId: string) => {
  // For unsaved new entries (temp IDs)
  if (pocId.startsWith("temp-")) {
    setLocalPocs((prev) => prev.filter((poc) => poc.id !== pocId));
    return;
  }

  // For existing POCs — send isDeleted:true
  handleDeletePoc(pocId);
};


  const handlePocFieldChange = (
    pocId: string,
    field: string,
    value: string
  ) => {

    const updatedPocs = localPocs.map((poc) => {
      if (poc.id === pocId) {
        return { ...poc, [field]: value };
      }
      return poc;
    });

    setLocalPocs(updatedPocs);
  };

  const handleCancelNewRow = () => {
    // Remove the temporary row
    setLocalPocs(localPocs.filter((poc) => !poc.id?.startsWith("temp-")));
    setIsAddingNew(false);
  };

  const handleDeletePoc = (pocId: string) => {
  if (!schoolId) return;

  const pocToDelete = localPocs.find((poc) => poc.id === pocId);
  if (!pocToDelete) return;

  // Prepare payload that matches your mutation definition
  const payload = {
    pocList: [
      {
        schoolId: schoolId,
        pocId: pocToDelete.id, // existing POC id
        pocName: pocToDelete.pocName,
        pocDesignation: pocToDelete.pocDesignation,
        pocPhone: pocToDelete.pocPhone,
        pocEmail: pocToDelete.pocEmail,
        isDeleted: true, // ✅ flag to mark deletion
      },
    ],
  };

  updateLabPocs(payload); // uses your axiosAuth.put under the hood

  // Update UI immediately (optimistic)
  setLocalPocs((prev) => prev.filter((poc) => poc.id !== pocId));
};




  const openAddPocDialog = () => {
    addPocForm.reset();
    setIsAddPocDialogOpen(true);
  };

  const closeAddPocDialog = () => {
    setIsAddPocDialogOpen(false);
    addPocForm.reset();
  };

  return {
    schoolPocs,
    isLoadingPocs,
    isAddPocDialogOpen,
    addPocForm,
    handleAddPoc,
    handleUpdatePocs,
    handleDeletePoc,
    openAddPocDialog,
    closeAddPocDialog,
    // Combined updating flag (generic + lab-specific)
    isUpdating: isUpdatingPocs || isUpdatingLabPocs || isCreatingLabPocDetails,
    // New local state and handlers
    localPocs,
    isAddingNew,
    validationErrors,
    handleSaveChanges,
    handleAddNewRow,
    handleDeletePocLocal,
    handlePocFieldChange,
    handleCancelNewRow,
    // Lab POC details mutation
    createLabPocDetails,
    isCreatingLabPocDetails,
  };
};
